import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { BASE_URL, HTTP_STATUS } from '../../utils/constant'
import { newAxiosInstance } from '../../../axios'

const initialState = {
    attendances:             [],
    status:                  HTTP_STATUS.IDLE,
    createStatus:            HTTP_STATUS.IDLE,
    updateStatus:            HTTP_STATUS.IDLE,
    deleteStatus:            HTTP_STATUS.IDLE,
    completeStatus:          HTTP_STATUS.IDLE,
    profileAttendances:      [],
    profileAttendanceStatus: HTTP_STATUS.IDLE,
}

export const fetchAttendances = createAsyncThunk('attendance/fetch', async (params, { rejectWithValue }) => {
    try {
        const response = await newAxiosInstance.get(`${BASE_URL}/api/Attendance?from=${params.from}&to=${params.to}`)
        return response.data
    } catch (err) {
        if (!err.response) throw err;
        return rejectWithValue(err.response.data)
    }
})

export const createAttendance = createAsyncThunk('attendance/create', async (postdata, { rejectWithValue }) => {
    try {
        const response = await newAxiosInstance.post(`${BASE_URL}/api/Attendance`, postdata)
        return response.data
    } catch (err) {
        if (!err.response) throw err;
        return rejectWithValue(err.response)
    }
})

export const updateAttendance = createAsyncThunk('attendance/update', async (postdata, { rejectWithValue }) => {
    try {
        const response = await newAxiosInstance.put(`${BASE_URL}/api/Attendance/${postdata.id}`, postdata)
        return response.data
    } catch (err) {
        if (!err.response) throw err;
        return rejectWithValue(err.response)
    }
})

export const deleteAttendance = createAsyncThunk('attendance/delete', async (id, { rejectWithValue }) => {
    try {
        const response = await newAxiosInstance.delete(`${BASE_URL}/api/Attendance/${id}`)
        return response.data
    } catch (err) {
        if (!err.response) throw err;
        return rejectWithValue(err.response.data)
    }
})

export const fetchProfileAttendances = createAsyncThunk('attendance/fetchByProfile', async ({ profileId, from, to }, { rejectWithValue }) => {
    try {
        const res = await newAxiosInstance.get(`${BASE_URL}/api/Attendance?from=${from}&to=${to}&workforceProfileId=${profileId}`)
        return res.data
    } catch (err) {
        if (!err.response) throw err
        return rejectWithValue(err.response.data)
    }
})

export const completeAttendanceByDate = createAsyncThunk('attendance/completeByDate', async ({ workforceProfileIds, date }, { rejectWithValue }) => {
    try {
        const response = await newAxiosInstance.post(`${BASE_URL}/api/Attendance/complete-by-date`, { workforceProfileIds, date })
        return response.data
    } catch (err) {
        if (!err.response) throw err;
        return rejectWithValue(err.response.data)
    }
})

// Returns aggregated attendance stats for a single workforce profile.
// Does not update slice state — callers use .unwrap() directly.
export const fetchAttendanceSummary = createAsyncThunk('attendance/fetchSummary', async (workforceProfileId, { rejectWithValue }) => {
    try {
        const response = await newAxiosInstance.get(`${BASE_URL}/api/Attendance/summary?workforceProfileId=${workforceProfileId}`)
        return response.data
    } catch (err) {
        if (!err.response) throw err;
        return rejectWithValue(err.response.data)
    }
})

export const Attendance = createSlice({
    name: 'attendance',
    initialState,
    reducers: {
        resetState: () => initialState,
    },
    extraReducers(builder) {
        builder
            .addCase(fetchAttendances.pending, (state) => {
                state.status = HTTP_STATUS.LOADING
            })
            .addCase(fetchAttendances.fulfilled, (state, action) => {
                state.status = HTTP_STATUS.SUCCEEDED
                state.attendances = action.payload.map(record => {
                    const name = record.workforceProfile?.name || '';
                    const dutyNames = (record.duties || []).map(d => d.label || d.name || '').join(', ');
                    return {
                        ...record,
                        title: `${name}${record.status ? ` — ${record.status}` : dutyNames ? ` — ${dutyNames}` : ''}`,
                        start: record.attendanceDate,
                        end:   record.attendanceDate,
                        total: record.netAmount,
                    };
                })
            })
            .addCase(fetchAttendances.rejected, (state) => {
                state.status = HTTP_STATUS.FAILED
            })
            .addCase(createAttendance.pending, (state) => {
                state.createStatus = HTTP_STATUS.LOADING
            })
            .addCase(createAttendance.fulfilled, (state) => {
                state.createStatus = HTTP_STATUS.SUCCEEDED
            })
            .addCase(createAttendance.rejected, (state) => {
                state.createStatus = HTTP_STATUS.FAILED
            })
            .addCase(updateAttendance.pending, (state) => {
                state.updateStatus = HTTP_STATUS.LOADING
            })
            .addCase(updateAttendance.fulfilled, (state) => {
                state.updateStatus = HTTP_STATUS.SUCCEEDED
            })
            .addCase(updateAttendance.rejected, (state) => {
                state.updateStatus = HTTP_STATUS.FAILED
            })
            .addCase(deleteAttendance.pending, (state) => {
                state.deleteStatus = HTTP_STATUS.LOADING
            })
            .addCase(deleteAttendance.fulfilled, (state) => {
                state.deleteStatus = HTTP_STATUS.SUCCEEDED
            })
            .addCase(deleteAttendance.rejected, (state) => {
                state.deleteStatus = HTTP_STATUS.FAILED
            })
            .addCase(fetchProfileAttendances.pending, (state) => {
                state.profileAttendanceStatus = HTTP_STATUS.LOADING
                state.profileAttendances = []
            })
            .addCase(fetchProfileAttendances.fulfilled, (state, action) => {
                state.profileAttendanceStatus = HTTP_STATUS.SUCCEEDED
                state.profileAttendances = action.payload
            })
            .addCase(fetchProfileAttendances.rejected, (state) => {
                state.profileAttendanceStatus = HTTP_STATUS.FAILED
            })
            .addCase(completeAttendanceByDate.pending, (state) => {
                state.completeStatus = HTTP_STATUS.LOADING
            })
            .addCase(completeAttendanceByDate.fulfilled, (state, action) => {
                state.completeStatus = HTTP_STATUS.SUCCEEDED
                const { workforceProfileIds, date } = action.meta.arg
                const idSet = new Set(workforceProfileIds)
                state.attendances = state.attendances.map(record =>
                    idSet.has(record.workforceProfile?.id) && record.attendanceDate === date
                        ? { ...record, isCompleted: true }
                        : record
                )
            })
            .addCase(completeAttendanceByDate.rejected, (state) => {
                state.completeStatus = HTTP_STATUS.FAILED
            })
    }
})

export const getAttendances               = (state) => state.attendance.attendances;
export const getAttendancesStatus         = (state) => state.attendance.status;
export const getAttendancesCreateStatus   = (state) => state.attendance.createStatus;
export const getAttendancesUpdateStatus   = (state) => state.attendance.updateStatus;
export const getDeleteAttendanceStatus    = (state) => state.attendance.deleteStatus;
export const getCompleteAttendanceStatus  = (state) => state.attendance.completeStatus;
export const getProfileAttendances        = (state) => state.attendance.profileAttendances;
export const getProfileAttendanceStatus   = (state) => state.attendance.profileAttendanceStatus;
export const AttendanceActions = Attendance.actions

export default Attendance.reducer
